import { useEffect, useRef, useState } from 'preact/hooks';
import { t } from '../i18n/index.js';
import { guestSession } from '../guestSession.js';
import { playChatSound } from '../audio/sounds.js';
import { EmojiPicker, insertAtCursor } from '../components/EmojiPicker.js';
import { SendButton } from '../components/SendButton.js';
import type { PmState } from '../chat/usePrivateMessages.js';
import { chatMaxLength, checkChatMessage } from '../../shared/chatRules.js';
import type { LobbyPlayer, MyRestrictions, PlayerRef, PublicUser } from '../../shared/protocol.js';

const time = (ms: number) => new Date(ms).toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' });
const dayTime = (ms: number) => {
  const d = new Date(ms);
  return d.toDateString() === new Date().toDateString()
    ? time(ms)
    : d.toLocaleString('fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const dateTime = (ms: number) => new Date(ms).toLocaleString('fi-FI', { dateStyle: 'short', timeStyle: 'short' });

/** True while the Messages page is on screen (no separate notifications then). */
export let messagesViewVisible = false;

/**
 * Private messages on their own page (decision 2026-09-29): all conversations of the last
 * 30 days with unread counts, a conversation, and a new message to any player – also to one who
 * is not online (they see it at their next login).
 */
export function MessagesView(props: { me: PublicUser; pm: PmState; players: LobbyPlayer[]; restrictions: MyRestrictions | null }) {
  const { pm, me } = props;
  const [active, setActive] = useState<PlayerRef | null>(null);
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState('');
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    messagesViewVisible = true;
    pm.refreshDirectory();
    return () => {
      messagesViewVisible = false;
      pm.view('messages', null);
    };
  }, []);
  useEffect(() => pm.view('messages', active?.id ?? null), [active?.id]);

  const open = (p: PlayerRef) => {
    setActive(p);
    setPicking(false);
    setSearch('');
    setError(null);
    pm.openConversation(p.id);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  // An incoming message plays the private message sound (the lobby chat is not on screen here).
  const lastFeed = pm.feed[pm.feed.length - 1];
  const firstRender = useRef(true);
  useEffect(() => {
    if (!firstRender.current && lastFeed && lastFeed.from.id !== me.id) playChatSound('chatPrivate');
    firstRender.current = false;
  }, [lastFeed?.id]);

  const messages = active ? (pm.messages.get(active.id) ?? []) : [];
  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [active?.id, messages.length]);

  const online = (id: number) => props.players.some((p) => p.id === id);
  const muted = props.restrictions?.chatMute ?? null;
  const maxLength = chatMaxLength(me.role);

  const send = async (e: Event) => {
    e.preventDefault();
    if (!active || busy) return;
    const body = text.trim();
    if (!body) return;
    const rule = checkChatMessage(body, me.role);
    if (rule) return setError(t.arena.errors[rule]);
    setBusy(true);
    const r = await pm.send(active.id, body);
    setBusy(false);
    if (r.ok) {
      setText('');
      setError(null);
    } else setError(t.arena.errors[r.error]);
  };

  const q = search.trim().toLocaleLowerCase('fi');
  const matches = pm.directory.filter((p) => !q || p.displayName.toLocaleLowerCase('fi').includes(q)).slice(0, 50);
  const m = t.messages;

  return (
    <section class={`card messages-page ${active ? 'has-active' : ''}`}>
      <div class="messages-list">
        <div class="section-head">
          <h2>{m.title}</h2>
          <button class="btn btn-small btn-primary" onClick={() => setPicking(!picking)} aria-expanded={picking}>
            {picking ? m.cancel : `✉ ${m.newMessage}`}
          </button>
        </div>
        {picking && (
          <div class="recipient-picker">
            <input
              value={search}
              onInput={(e) => setSearch(e.currentTarget.value)}
              placeholder={m.searchPlaceholder}
              aria-label={m.searchPlaceholder}
              autofocus
            />
            <ul>
              {matches.map((p) => (
                <li key={p.id}>
                  <button class="recipient" onClick={() => open(p)}>
                    <span class={`status-dot ${online(p.id) ? 'status-available' : 'status-offline'}`} />
                    {p.displayName}
                  </button>
                </li>
              ))}
              {matches.length === 0 && <li class="muted">{m.noMatches}</li>}
            </ul>
          </div>
        )}
        {pm.conversations.length === 0 && !picking && <p class="muted">{m.empty}</p>}
        <ul class="conversation-list">
          {pm.conversations.map((c) => (
            <li key={c.with.id}>
              <button class={`conversation ${active?.id === c.with.id ? 'active' : ''} ${c.unread > 0 ? 'unread' : ''}`} onClick={() => open(c.with)}>
                <span class={`status-dot ${online(c.with.id) ? 'status-available' : 'status-offline'}`} />
                <span class="conversation-name">{c.with.displayName}</span>
                <span class="conversation-time">{dayTime(c.lastAt)}</span>
                {c.unread > 0 && <span class="tab-unread">{c.unread}</span>}
              </button>
            </li>
          ))}
        </ul>
        <p class="hint">{m.retention}</p>
      </div>

      <div class="messages-conversation">
        {!active ? (
          <p class="muted chat-empty">{m.choose}</p>
        ) : (
          <>
            <div class="conversation-head">
              <button class="btn btn-ghost btn-small back" onClick={() => setActive(null)}>
                ← {m.back}
              </button>
              <h3>
                <span class={`status-dot ${online(active.id) ? 'status-available' : 'status-offline'}`} /> {active.displayName}
              </h3>
            </div>
            {!online(active.id) && <p class="notice">{t.lobbyChat.offline(active.displayName)}</p>}
            {muted && <p class="error">{t.restrictions.chatMuted(muted.until ? dateTime(muted.until) : null)}</p>}
            <div class="lobby-messages conversation-messages" ref={listRef}>
              {messages.length === 0 && <p class="muted chat-empty">{t.lobbyChat.pmEmpty}</p>}
              {messages.map((msg) => (
                <div class={`chat-line ${msg.from.id === me.id ? 'mine' : ''}`} key={msg.id}>
                  <span class="chat-time">{dayTime(msg.sentAt)}</span>{' '}
                  <span class={`chat-nick role-${msg.fromRole}`}>{msg.from.id === me.id ? t.lobbyChat.you : msg.from.displayName}</span>:{' '}
                  <span class="chat-text">{msg.text}</span>
                </div>
              ))}
            </div>
            {error && <p class="error">{error}</p>}
            {guestSession.guest ? (
              <p class="chat-readonly">{t.guest.readOnly}</p>
            ) : (
              <form class="chat-form" onSubmit={send}>
                <div class="chat-input-row">
                  <input
                    ref={inputRef}
                    value={text}
                    onInput={(e) => {
                      setText(e.currentTarget.value);
                      setError(null);
                    }}
                    placeholder={t.lobbyChat.pmPlaceholder(active.displayName)}
                    maxLength={maxLength}
                    disabled={Boolean(muted)}
                    aria-label={t.lobbyChat.pmPlaceholder(active.displayName)}
                  />
                  <SendButton label={t.chatUi.sendPm} private disabled={Boolean(muted) || busy || !text.trim()} />
                </div>
                <div class="chat-tools">
                  <EmojiPicker disabled={Boolean(muted)} onPick={(emoji) => setText(insertAtCursor(inputRef.current, text, emoji))} />
                  <span class="char-count" aria-hidden="true">
                    {text.trim().length}/{maxLength}
                  </span>
                </div>
              </form>
            )}
            <p class="hint privacy-note">{t.lobbyChat.privacy}</p>
          </>
        )}
      </div>
    </section>
  );
}
